import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  MessageSquare, Send, ChevronRight, ChevronLeft, Copy, CheckCircle2, Sparkles, Save, Inbox, PauseCircle, Play, Wand2,
} from "lucide-react";
import { ProfileTags } from "@/components/smartleads/ProfileTags";
import { EmailDraftPane } from "@/components/smartleads/EmailDraftPane";

const ReplyBanner = ({ lead, sl }) => (
  <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-800 flex items-start gap-2" data-testid="draft-replied-banner">
    <PauseCircle className="h-4 w-4 mt-0.5 shrink-0" />
    <div className="flex-1">
      <b>Replied — follow-ups paused.</b>{lead.reply_snippet ? ` “${lead.reply_snippet}”` : ""} Handle the conversation in Gingr, then resume if needed.
    </div>
    <Button size="sm" variant="outline" className="h-7 border-rose-300 text-rose-700" onClick={() => sl.resume(lead.id)} data-testid="draft-resume-btn">
      <Play className="h-3 w-3 mr-1" />Resume
    </Button>
  </div>
);

// Review AI drafts for one or many leads: edit + save, rewrite with a note, then
// add to outbox / send now (Gingr) or copy + mark sent manually (not in Gingr).
export const SmartDraftDialog = ({ ids, getLead, sl, onClose, onChanged }) => {
  const [idx, setIdx] = useState(0);
  const [text, setText] = useState("");
  const [saved, setSaved] = useState("");
  const [edited, setEdited] = useState(false);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({});
  const [channel, setChannel] = useState("sms");
  const id = ids?.[idx];
  const dogLabel = meta.tags?.find((t) => t.kind === "dog")?.label;
  const lead = id ? getLead(id) : null;

  const load = async (leadId, regenerate = false, n = "") => {
    setLoading(true);
    const d = await sl.getDraft(leadId, regenerate, n);
    setLoading(false);
    if (d) {
      setText(d.text || ""); setSaved(d.text || ""); setEdited(!!d.edited);
      setMeta({ tags: d.tags, prefersEmail: d.prefers_email, hasEmail: d.has_email, email: d.email });
      if (!regenerate && d.prefers_email && d.has_email) setChannel("email");
    }
  };

  useEffect(() => { setIdx(0); }, [ids]);
  useEffect(() => { if (id) { setText(""); setNote(""); setMeta({}); setChannel("sms"); load(id); } }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const next = () => (idx + 1 >= ids.length ? onClose() : setIdx(idx + 1));
  const dirty = text.trim() !== saved.trim();
  const paused = !!lead?.paused;
  const done = (r) => { if (r) { onChanged?.(); next(); } };

  const save = async () => { const r = await sl.saveDraft(id, text.trim()); if (r) { setSaved(r.text); setEdited(true); } };
  const rewrite = async () => { await load(id, true, note.trim()); setNote(""); };

  return (
    <Dialog open={!!ids?.length} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="smart-draft-dialog">
        {lead && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-cyan-600" /> {lead.name}{dogLabel ? ` · ${dogLabel}` : ""}
              </DialogTitle>
              <DialogDescription className="sr-only">Review, edit and send the AI-drafted follow-up text</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-[12px] gap-2 flex-wrap">
                <span className="text-gray-500" data-testid="draft-position">{idx + 1} of {ids.length}</span>
                <div className="flex gap-1.5">
                  {edited && <Badge variant="outline" className="text-[10px]" data-testid="draft-edited-badge">Edited by rep</Badge>}
                  {lead.gingr
                    ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid="draft-channel-badge">{sl.meta.live ? "Sends via Gingr" : "Gingr · dry run"}</Badge>
                    : <Badge className="bg-amber-100 text-amber-800 border-amber-200" data-testid="draft-channel-badge">Not in Gingr — send from your phone</Badge>}
                </div>
              </div>
              {paused && <ReplyBanner lead={lead} sl={sl} />}
              <ProfileTags tags={meta.tags} />
              {meta.hasEmail && (
                <div className="inline-flex rounded-lg border border-slate-200 p-0.5 text-[12px]" data-testid="draft-channel-toggle">
                  {[["sms", "Text (SMS)"], ["email", "Email"]].map(([k, label]) => (
                    <button key={k} onClick={() => setChannel(k)} data-testid={`draft-channel-${k}`}
                      className={`px-3 py-1 rounded-md font-semibold transition-colors ${channel === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                      {label}{k === "email" && meta.prefersEmail ? " · preferred" : ""}
                    </button>
                  ))}
                </div>
              )}
              {channel === "email" && <EmailDraftPane id={id} email={meta.email} sl={sl} paused={paused} onLogged={() => done(true)} />}
              {channel === "sms" && (lead.nba || lead.why) && (
                <p className="text-[12px] text-gray-500"><Sparkles className="inline h-3 w-3 text-amber-500 mr-1" />
                  {lead.why}{lead.nba ? <> · <span className="font-medium text-cyan-700">{lead.nba}</span></> : null}</p>
              )}
              {channel === "sms" && <>
              <Textarea value={text} maxLength={320} onChange={(e) => setText(e.target.value)} rows={5} disabled={loading}
                placeholder={loading ? "Drafting a warm, human message…" : ""} data-testid="draft-textarea" />
              <div className="flex items-center justify-between text-[11px] text-gray-400">
                <span>{dirty ? "Unsaved edits" : "Edit freely — your version is kept for this lead"}</span>
                <div className="flex items-center gap-2">
                  <span className={text.length > 300 ? "text-rose-500" : ""} data-testid="char-count">{text.length}/320</span>
                  <Button size="sm" variant="outline" className="h-7" disabled={!dirty || !text.trim() || sl.busy === "save"} onClick={save} data-testid="draft-save-btn">
                    <Save className="h-3 w-3 mr-1" />Save edit
                  </Button>
                </div>
              </div>
              <div className="flex gap-2">
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Tell AI what to add (e.g. mention we have openings that week)"
                  className="h-8 text-[12px]" data-testid="draft-note-input" />
                <Button size="sm" variant="outline" className="h-8 shrink-0 border-amber-300 text-amber-700" disabled={loading} onClick={rewrite} data-testid="draft-rewrite-btn">
                  <Wand2 className="h-3 w-3 mr-1" />{note.trim() ? "Rewrite" : "New draft"}
                </Button>
              </div>
              </>}
            </div>
            <DialogFooter className="flex sm:justify-between gap-2 flex-wrap">
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => setIdx(idx - 1)} disabled={idx === 0} data-testid="draft-prev"><ChevronLeft className="h-4 w-4" /></Button>
                <Button variant="ghost" size="sm" onClick={next} data-testid="draft-skip">Skip <ChevronRight className="h-4 w-4 ml-1" /></Button>
              </div>
              {channel === "email" ? null : lead.gingr ? (
                <div className="flex gap-2">
                  <Button variant="outline" disabled={paused || loading || !text.trim() || !!sl.busy} onClick={async () => done(await sl.sendNow(id, text.trim(), onChanged))} data-testid="draft-send-now-btn">
                    <Send className="h-4 w-4 mr-1.5" />Send now
                  </Button>
                  <Button className="bg-gradient-to-r from-emerald-500 to-emerald-600" disabled={paused || loading || !text.trim() || !!sl.busy}
                    onClick={async () => { const ok = await sl.addToOutbox(id, text.trim()); if (ok) toast.success(`Added ${lead.name} to the outbox`); done(ok); }} data-testid="draft-add-outbox-btn">
                    <Inbox className="h-4 w-4 mr-1.5" />Add to outbox
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button variant="outline" disabled={!text} onClick={() => { navigator.clipboard?.writeText(text); toast.success("Copied"); }} data-testid="draft-copy-btn">
                    <Copy className="h-4 w-4 mr-1.5" />Copy
                  </Button>
                  <Button className="bg-gradient-to-r from-cyan-500 to-blue-600" disabled={loading || !text.trim() || !!sl.busy}
                    onClick={async () => done(await sl.logAttempt(id, "text", text.trim()))} data-testid="draft-mark-sent-btn">
                    <CheckCircle2 className="h-4 w-4 mr-1.5" />Mark as sent manually
                  </Button>
                </div>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
